(function () {
  const STORAGE_KEY = "fabrique_ai_size_guide";
  const LEGACY_STORAGE_PREFIXES = [
    "fabrique_ai_size_guide__",
    "fabrique_ai_size_guide_",
  ];

  const escapeForRegex = (value) => value.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

  const readCookie = (name) => {
    const match = document.cookie.match(new RegExp(`(?:^|; )${escapeForRegex(name)}=([^;]*)`));
    return match ? decodeURIComponent(match[1]) : "";
  };

  const writeCookie = (name, value, days) => {
    let expires = "";

    if (days) {
      const date = new Date();
      date.setTime(date.getTime() + days * 24 * 60 * 60 * 1000);
      expires = `; expires=${date.toUTCString()}`;
    }

    document.cookie = `${name}=${encodeURIComponent(value)}${expires}; path=/`;
  };

  const removeCookie = (name) => {
    document.cookie = `${name}=; Max-Age=-99999999; path=/`;
  };

  const toTitleCase = (value) =>
    String(value || "")
      .replace(/_/g, " ")
      .replace(/\b\w/g, (char) => char.toUpperCase());

  const BRAND_LABEL_MAP = {
    jcrew: "J.Crew",
  };
  const SIZE_MAPPING_METADATA_KEYS = new Set(["petite_size_note"]);
  const SIZE_RECOMMEND_MODULE_TYPE = "popup_window";

  const getMappingEntries = (config) => Object.entries(config?.size_mapping || {});
  const isSizeMappingMetadataKey = (key) =>
    SIZE_MAPPING_METADATA_KEYS.has(String(key || "").trim().toLowerCase());

  const getSelectableMappingEntries = (mapping) =>
    Object.entries(mapping || {}).filter(([key, value]) => !isSizeMappingMetadataKey(key) && value);

  const toSizeValues = (value) => {
    const values = Array.isArray(value) ? value : [value];

    return values
      .map((item) => String(item ?? "").trim())
      .filter(Boolean);
  };
  
  const getBrandKeys = (config) => {
    const keys = new Set();

    getMappingEntries(config).forEach(([, mapping]) => {
      getSelectableMappingEntries(mapping).forEach(([key]) => keys.add(key));
    });

    return Array.from(keys);
  };

  const getSizesForBrand = (config, brandKey) =>
      getMappingEntries(config).flatMap(([, mapping]) => toSizeValues(mapping?.[brandKey]));

  const getAllSizeValues = (config) => {
    const sizes = new Set();

    getMappingEntries(config).forEach(([, mapping]) => {
      getSelectableMappingEntries(mapping).forEach(([, sizeValue]) => {
        toSizeValues(sizeValue).forEach((size) => sizes.add(size));
      });
    });

    return Array.from(sizes);
  };

  const getRecommendationDetails = (config, brandKey, brandSize) => {
    if (!brandKey || !brandSize) {
      return {
        recommendedSize: "",
        petiteSizeNote: "",
      };
    }

    const normalizedBrandSize = String(brandSize || "").trim().toLowerCase();
    const matchedEntry = getMappingEntries(config).find(([, mapping]) => {
     return toSizeValues(mapping?.[brandKey]).some(
        (mappedValue) => mappedValue.toLowerCase() === normalizedBrandSize
      );
    });

    if (!matchedEntry) {
      return {
        recommendedSize: "",
        petiteSizeNote: "",
      };
    }

    return {
      recommendedSize: matchedEntry[0] || "",
      petiteSizeNote: matchedEntry[1]?.petite_size_note || "",
    };
  };

  const findRecommendedSize = (config, brandKey, brandSize) => {
    return getRecommendationDetails(config, brandKey, brandSize).recommendedSize;
  };

  const parseStoredValue = (value) => {
    if (!value) return null;

    try {
      const parsed = JSON.parse(value);
      if (parsed && typeof parsed === "object") {
        return parsed;
      }
    } catch (error) {
      return null;
    }

    return null;
  };

  const getLegacyCookieKeys = () =>
    (document.cookie ? document.cookie.split("; ") : [])
      .map((cookieEntry) => cookieEntry.split("=")[0])
      .filter((cookieName) =>
        LEGACY_STORAGE_PREFIXES.some((prefix) => cookieName.startsWith(prefix))
      );

  const getCookieCandidates = () => [
    STORAGE_KEY,
    `${STORAGE_KEY}_selection`,
    `${STORAGE_KEY}_size`,
    ...getLegacyCookieKeys(),
    "fabrique_size_brand_selection",
    "fabrique_brand_size_selection",
    "ai_size_guide",
  ];

  const getStoredSelection = () => {
    const candidates = getCookieCandidates();

    for (const key of candidates) {
      const parsed = parseStoredValue(readCookie(key));
      if (parsed?.brand && parsed?.size) {
        return parsed;
      }
    }

    return null;
  };

  const persistSelection = (selection) => {
    writeCookie(STORAGE_KEY, JSON.stringify(selection), 180);
  };

  const clearSelectionCookies = () => {
    getCookieCandidates().forEach(removeCookie);
  };

  const createOptionButton = (label, value, isActive) => {
    const button = document.createElement("button");
    button.type = "button";
    button.className = "fabrique-ai-size-guide__option";
    button.textContent = label;
    button.dataset.value = value;
    if (isActive) {
      button.classList.add("is-active");
      button.setAttribute("aria-pressed", "true");
    } else {
      button.setAttribute("aria-pressed", "false");
    }

    return button;
  };

  const getDialog = (root) => {
    const dialogId = root.dataset.dialogId;
    return dialogId ? document.getElementById(dialogId) : null;
  };

  const getFrequentlyWornDialog = (root) => {
    const dialogId = root.dataset.frequentlyWornDialogId;

    return dialogId
      ? document.getElementById(dialogId)
      : root.querySelector("[data-ai-size-guide-frequently-worn-dialog]");
  };

  const queryGuideNode = (root, selector) =>
    root.querySelector(selector) || getDialog(root)?.querySelector(selector);

  const mountDialogToBody = (root) => {
    const dialog = getDialog(root);
    if (!dialog || dialog.dataset.mountedToBody === "true") return;

    dialog.dataset.mountedToBody = "true";
    document.body.appendChild(dialog);
  };

  const mountFrequentlyWornDialogToBody = (root) => {
    const dialog = getFrequentlyWornDialog(root);
    if (!dialog || dialog.dataset.mountedToBody === "true") return;

    dialog.dataset.mountedToBody = "true";
    document.body.appendChild(dialog);
  };

  const getBrandLabel = (brandKey) => {
    const normalizedKey = String(brandKey || "").replace(/_size_code$/i, "");

    return BRAND_LABEL_MAP[normalizedKey.toLowerCase()] || toTitleCase(normalizedKey);
  };

  const getCurrentProductTrackItems = (root) => {
    const handle = document.querySelector("input[data-good-handle]")?.value || "";
    const styleNo = document.querySelector("input[data-style-no]")?.value || "";
    const name = document.querySelector("input[data-good-title]")?.value || "";

    if (!(handle || styleNo || name)) {
      return [];
    }

    return [
      {
        handle,
        styleNo,
        name,
      },
    ];
  };

  const trackSizeRecommendClick = (root, moduleId, extraPayload = {}) => {
    if (!window.$track?.track) {
      return;
    }

    window.$track.track("click", {
      event: "WebClick",
      moduleId,
      moduleType: SIZE_RECOMMEND_MODULE_TYPE,
      trackItems: getCurrentProductTrackItems(root),
      ...extraPayload,
    });
  };

  const buildRecommendQueryParam = (brandKey, userSize, recommendedSize) =>
    JSON.stringify({
      brand: getBrandLabel(brandKey),
      userSize: userSize || "",
      recommend_size: recommendedSize || "",
    });

  const setError = (root, message = "") => {
    const errorNode = queryGuideNode(root, "[data-ai-size-guide-error]");
    if (!errorNode) return;

    errorNode.textContent = message || "Please select brand and size";
    errorNode.hidden = !message;
  };

  const isResultViewVisible = (root) =>
    !queryGuideNode(root, "[data-ai-size-guide-result-view]")?.hasAttribute("hidden");

  const showSelectionView = (root) => {
    queryGuideNode(root, "[data-ai-size-guide-selection-view]")?.removeAttribute("hidden");
    queryGuideNode(root, "[data-ai-size-guide-result-view]")?.setAttribute("hidden", "hidden");
  };

  const updateSelectionNote = (root, note = "") => {
    const noteTarget = queryGuideNode(root, "[data-ai-size-guide-selection-note]");

    if (!noteTarget) return;

    noteTarget.textContent = note;
    noteTarget.hidden = !note;
  };

  const showResultView = (root, selection, recommendedSize, petiteSizeNote = "") => {
    const sizeTarget = queryGuideNode(root, "[data-ai-size-guide-result-size]");
    const copyTarget = queryGuideNode(root, "[data-ai-size-guide-result-copy]");
    const brandLabel = getBrandLabel(selection.brand);

    if (sizeTarget) sizeTarget.textContent = recommendedSize;
    if (copyTarget) {
      copyTarget.textContent =
        petiteSizeNote || `Base on your ${brandLabel} bottoms size ${selection.size}`;
    }

    queryGuideNode(root, "[data-ai-size-guide-selection-view]")?.setAttribute("hidden", "hidden");
    queryGuideNode(root, "[data-ai-size-guide-result-view]")?.removeAttribute("hidden");
  };

  const toggleSubDescVisibility = (root, recommendedSize) => {
    const subDesc = root.parentElement?.querySelector(".fabrique-detail-subDesc");

    if (!subDesc) return;

    subDesc.hidden = Boolean(recommendedSize);
  };

  const updateInlineResult = (root, recommendedSize, petiteSizeNote = "") => {
    const emptyState = root.querySelector("[data-ai-size-guide-empty]");
    const result = root.querySelector("[data-ai-size-guide-result]");
    const sizeTarget = root.querySelector("[data-ai-size-guide-size]");
    const copyTarget = root.querySelector("[data-ai-size-guide-inline-copy]");

    if (recommendedSize) {
      if (sizeTarget) sizeTarget.textContent = recommendedSize;
      if (copyTarget) {
        copyTarget.textContent = petiteSizeNote;
        copyTarget.hidden = !petiteSizeNote;
      }
      if (emptyState) emptyState.hidden = true;
      if (result) result.hidden = false;
      toggleSubDescVisibility(root, recommendedSize);
      return;
    }

    if (sizeTarget) sizeTarget.textContent = "";
    if (copyTarget) {
      copyTarget.textContent = "";
      copyTarget.hidden = true;
    }
    if (emptyState) emptyState.hidden = false;
    if (result) result.hidden = true;
    toggleSubDescVisibility(root, "");
  };

  const renderSizeOptions = (root, config, state) => {
    const sizeContainer = queryGuideNode(root, "[data-ai-size-guide-sizes]");
    if (!sizeContainer) return;

    sizeContainer.innerHTML = "";

    const displayBrand = state.brand || getBrandKeys(config)[0] || "";
    const sizeValues = Array.from(new Set(getSizesForBrand(config, displayBrand)));

    sizeValues.forEach((sizeValue) => {
      const button = createOptionButton(sizeValue, sizeValue, state.size === sizeValue);

      button.addEventListener("click", () => {
        state.size = sizeValue;
        setError(root, "");
        renderSizeOptions(root, config, state);
        updateSelectionNote(
          root,
          getRecommendationDetails(config, state.brand, state.size).petiteSizeNote
        );
      });

      sizeContainer.appendChild(button);
    });
  };

  const renderBrandOptions = (root, config, state) => {
    const brandContainer = queryGuideNode(root, "[data-ai-size-guide-brands]");
    if (!brandContainer) return;

    brandContainer.innerHTML = "";

    getBrandKeys(config).forEach((brandKey) => {
      const button = createOptionButton(getBrandLabel(brandKey), brandKey, state.brand === brandKey);

      button.addEventListener("click", () => {
        state.brand = brandKey;
        const validSizes = getSizesForBrand(config, brandKey);

        if (!validSizes.includes(state.size)) {
          state.size = "";
        }

        setError(root, "");
        renderBrandOptions(root, config, state);
        renderSizeOptions(root, config, state);
        updateSelectionNote(
          root,
          getRecommendationDetails(config, state.brand, state.size).petiteSizeNote
        );
      });

      brandContainer.appendChild(button);
    });
  };

  const syncStateFromStoredSelection = (root, config, state) => {
    const storedSelection = getStoredSelection();
    const recommendationDetails = storedSelection
      ? getRecommendationDetails(config, storedSelection.brand, storedSelection.size)
      : {
          recommendedSize: "",
          petiteSizeNote: "",
        };
    const storedRecommendedSize = recommendationDetails.recommendedSize;
    const storedPetiteSizeNote = recommendationDetails.petiteSizeNote;

    state.brand = storedSelection?.brand || "";
    state.size = storedSelection?.size || "";
    state.lastRecommendedSize = storedRecommendedSize;
    state.lastPetiteSizeNote = storedPetiteSizeNote;

    if (state.brand && state.size) {
      root.dataset.aiSizeGuideBrand = state.brand;
      root.dataset.aiSizeGuideBrandSize = state.size;
    } else {
      root.dataset.aiSizeGuideBrand = "";
      root.dataset.aiSizeGuideBrandSize = "";
    }

    return {
      storedSelection,
      storedRecommendedSize,
      storedPetiteSizeNote,
    };
  };

  const openDialog = (root, mode = "selection", selection = null, recommendedSize = "") => {
    const dialog = getDialog(root);
    const panel = dialog?.querySelector(".fabrique-ai-size-guide__dialog-panel");
    if (!dialog) return;

    dialog.hidden = false;
    document.body.classList.add("overflow-hidden");

    if (mode === "result" && selection && recommendedSize) {
      showResultView(root, selection, recommendedSize);
    } else {
      showSelectionView(root);
    }

    requestAnimationFrame(() => panel?.focus());
  };

  const closeDialog = (root, shouldRefresh = false) => {
    const dialog = getDialog(root);
    if (!dialog) return;

    dialog.hidden = true;
    document.body.classList.remove("overflow-hidden");

    if (shouldRefresh) {
      // window.location.reload();
    }
  };

  const updateFrequentlyWornSubmitState = (root) => {
    const input = getFrequentlyWornDialog(root)?.querySelector(
      "[data-ai-size-guide-frequently-worn-input]"
    );
    const submitButton = getFrequentlyWornDialog(root)?.querySelector(
      "[data-ai-size-guide-frequently-worn-submit]"
    );

    if (!submitButton) return;

    submitButton.disabled = !input?.value.trim();
  };

  const openFrequentlyWornDialog = (root) => {
    const dialog = getFrequentlyWornDialog(root);
    const panel = dialog?.querySelector(".fabrique-ai-size-guide__dialog-panel");
    const input = dialog?.querySelector("[data-ai-size-guide-frequently-worn-input]");
    if (!dialog) return;

    if (input) input.value = "";
    updateFrequentlyWornSubmitState(root);
    dialog.hidden = false;
    document.body.classList.add("overflow-hidden");

    requestAnimationFrame(() => {
      panel?.focus();
      input?.focus();
    });
  };

  const closeFrequentlyWornDialog = (root) => {
    const dialog = getFrequentlyWornDialog(root);
    if (!dialog) return;

    dialog.hidden = true;
    document.body.classList.remove("overflow-hidden");
  };

  const initializeRoot = (root) => {
    if (!root || root.dataset.initialized) return;

    root.dataset.initialized = "pending";

    const configElement = root.querySelector("[data-ai-size-guide-data]");
    if (!configElement) return;

    let config;

    try {
      config = JSON.parse(configElement.textContent);
    } catch (error) {
      return;
    }

    if (!config?.size_mapping) return;

    root.dataset.initialized = "true";
    mountDialogToBody(root);
    mountFrequentlyWornDialogToBody(root);

    const state = {
      brand: "",
      size: "",
      lastRecommendedSize: "",
      lastPetiteSizeNote: "",
    };

    const { storedRecommendedSize, storedPetiteSizeNote } = syncStateFromStoredSelection(
      root,
      config,
      state
    );

    renderBrandOptions(root, config, state);
    renderSizeOptions(root, config, state);
    updateSelectionNote(root, storedPetiteSizeNote);
    updateInlineResult(root, storedRecommendedSize, storedPetiteSizeNote);
    setError(root, "");
    showSelectionView(root);
    root.hidden = false;

    root.querySelector("[data-ai-size-guide-empty]")?.addEventListener("click", () => {
      trackSizeRecommendClick(root, "size_recommend");
      syncStateFromStoredSelection(root, config, state);
      renderBrandOptions(root, config, state);
      renderSizeOptions(root, config, state);
      updateSelectionNote(root, state.lastPetiteSizeNote);
      setError(root, "");
      openDialog(root, "selection");
    });

    root.querySelector("[data-ai-size-guide-open]")?.addEventListener("click", () => {
      trackSizeRecommendClick(root, "size_recommend");
      syncStateFromStoredSelection(root, config, state);
      renderBrandOptions(root, config, state);
      renderSizeOptions(root, config, state);
      updateSelectionNote(root, state.lastPetiteSizeNote);
      setError(root, "");
      openDialog(root, "selection");
    });

    queryGuideNode(root, "[data-ai-size-guide-close]")?.addEventListener("click", () => {
      closeDialog(root, isResultViewVisible(root));
    });

    queryGuideNode(root, "[data-ai-size-guide-clear]")?.addEventListener("click", () => {
      trackSizeRecommendClick(root, "quit_size_recommend");
      clearSelectionCookies();
      state.brand = "";
      state.size = "";
      state.lastRecommendedSize = "";
      state.lastPetiteSizeNote = "";
      root.dataset.aiSizeGuideBrand = "";
      root.dataset.aiSizeGuideBrandSize = "";
      updateSelectionNote(root, "");
      updateInlineResult(root, "", "");
      closeDialog(root, true);
      openFrequentlyWornDialog(root);
    });

    queryGuideNode(root, "[data-ai-size-guide-result-close]")?.addEventListener("click", () => {
      closeDialog(root, true);
    });

    getDialog(root)?.addEventListener("click", (event) => {
      if (event.target === event.currentTarget) {
        closeDialog(root, isResultViewVisible(root));
      }
    });

    getDialog(root)?.addEventListener("keyup", (event) => {
      if (event.code?.toUpperCase() === "ESCAPE") {
        closeDialog(root, isResultViewVisible(root));
      }
    });

    getFrequentlyWornDialog(root)
      ?.querySelector("[data-ai-size-guide-frequently-worn-input]")
      ?.addEventListener("input", () => {
        updateFrequentlyWornSubmitState(root);
      });

    getFrequentlyWornDialog(root)
      ?.querySelector("[data-ai-size-guide-frequently-worn-close]")
      ?.addEventListener("click", () => {
        closeFrequentlyWornDialog(root);
      });

    getFrequentlyWornDialog(root)?.addEventListener("click", (event) => {
      if (event.target === event.currentTarget) {
        closeFrequentlyWornDialog(root);
      }
    });

    getFrequentlyWornDialog(root)?.addEventListener("keyup", (event) => {
      if (event.code?.toUpperCase() === "ESCAPE") {
        closeFrequentlyWornDialog(root);
      }
    });

    getFrequentlyWornDialog(root)
      ?.querySelector("[data-ai-size-guide-frequently-worn-submit]")
      ?.addEventListener("click", () => {
        const input = getFrequentlyWornDialog(root)?.querySelector(
          "[data-ai-size-guide-frequently-worn-input]"
        );
        const frequentlyWornBrands = input?.value.trim() || "";

        if (!frequentlyWornBrands) {
          updateFrequentlyWornSubmitState(root);
          return;
        }

        trackSizeRecommendClick(root, "brands_frequently_worn", {
          queryParam: frequentlyWornBrands,
        });
        closeFrequentlyWornDialog(root);
      });

    queryGuideNode(root, "[data-ai-size-guide-apply]")?.addEventListener("click", () => {
      if (!(state.brand && state.size)) {
        setError(root, "Please select brand and size");
        return;
      }

      const recommendationDetails = getRecommendationDetails(config, state.brand, state.size);
      const recommendedSize = recommendationDetails.recommendedSize;
      const petiteSizeNote = recommendationDetails.petiteSizeNote;
      const selection = {
        brand: state.brand,
        size: state.size,
      };
      const queryParam = buildRecommendQueryParam(state.brand, state.size, recommendedSize);

      trackSizeRecommendClick(root, "do_size_recommend", {
        queryParam,
      });
      persistSelection(selection);
      state.lastRecommendedSize = recommendedSize;
      state.lastPetiteSizeNote = petiteSizeNote;
      root.dataset.aiSizeGuideBrand = state.brand;
      root.dataset.aiSizeGuideBrandSize = state.size;
      updateInlineResult(root, recommendedSize, petiteSizeNote);
      setError(root, "");
      showResultView(root, selection, recommendedSize, petiteSizeNote);
    });
  };

  const initializeAll = (scope = document) => {
    scope.querySelectorAll("[data-ai-size-guide]").forEach(initializeRoot);
  };

  document.addEventListener("DOMContentLoaded", () => initializeAll());
})();
